import { Box, useTheme } from '@chronosphereio/chrono-ui';
import { AbsoluteTimeRange } from '@chronosphereio/core';
import { useEffect, useMemo, useRef } from 'react';
import { EventItem, isEventWithEndTimestamp } from '@/core';
import { EventTooltip } from '../EventTooltip';
import { PanelTooltipWrapper } from '../PanelTooltip';
import { usePanelTooltipActions, usePanelTooltipState } from '../PanelTooltip/PanelTooltipProvider';
import { EventIcon } from './EventIcon';

/** Default height of the timeline */
export const EVENT_TIMELINE_HEIGHT = 32;

/** Y offset of icons, used to center the icons vertically in the timeline */
const EVENT_Y_OFFSET = 16;

/** Minimum width of the shaded range for grouped events */
const RANGE_MIN_WIDTH_PX = 2;

/** Opacity of the shaded range for grouped events */
const RANGE_OPACITY = 0.15;

/** Buffer around event icons so that the tooltip shows when not hovering directly over the icon */
const EVENT_HIT_RADIUS_PX = 14;

type EventPosition = {
  /** Event data (either single event or grouped event) */
  event: EventItem;
  /** X position of the event icon */
  x: number;
  /** Y position of the event icon */
  y: number;
  /** Whether this is the end marker for a grouped event */
  isEndMarker?: boolean;
};
const EMPTY_EVENT_POSITIONS: EventPosition[] = [];

export interface EventTimelineProps {
  events: EventItem[];
  timeRange: AbsoluteTimeRange;
  width: number;
  /**
   * Height of timeline (default: 32px)
   */
  height?: number;
  /**
   * Callback when an event icon is clicked
   */
  onEventClick?: (event: EventItem) => void;
  /**
   * Parent panel key to be used to create unique panel key for event tooltip.
   * Used to restore correct hover state when mouse leaves event timeline and returns to graph.
   */
  parentPanelKey?: string;
}

/**
 * Renders a horizontal timeline of events (useful for displaying single and grouped events above a chart).
 * Only visible when events are present.
 */
export function EventTimeline({
  events,
  timeRange,
  width,
  height = EVENT_TIMELINE_HEIGHT,
  onEventClick,
  parentPanelKey,
}: EventTimelineProps) {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const { mousePos, pinnedPanelKey } = usePanelTooltipState();
  const { handleUnpin } = usePanelTooltipActions();
  const lastDetectedEventRef = useRef<EventItem | null>(null);

  const timeRangeMs = useMemo(() => timeRange.end.getTime() - timeRange.start.getTime(), [timeRange]);

  const eventPositions: EventPosition[] = useMemo(() => {
    if (!events || events.length === 0 || timeRangeMs <= 0) {
      return EMPTY_EVENT_POSITIONS;
    }
    const positions: EventPosition[] = [];
    for (const event of events) {
      const eventTimestamp = event.timestampMs;
      // Filter events outside visible range
      if (eventTimestamp < timeRange.start.getTime() || eventTimestamp > timeRange.end.getTime()) {
        continue;
      }
      // Add icon at start timestamp
      const startX = ((eventTimestamp - timeRange.start.getTime()) / timeRangeMs) * width;
      positions.push({
        event,
        x: startX,
        y: EVENT_Y_OFFSET,
      });
      // Add icon at end for events with endTimestamp
      if (isEventWithEndTimestamp(event) && event.endTimestampMs <= timeRange.end.getTime()) {
        const endX = ((event.endTimestampMs - timeRange.start.getTime()) / timeRangeMs) * width;
        positions.push({
          event,
          x: endX,
          y: EVENT_Y_OFFSET,
          isEndMarker: true,
        });
      }
    }
    return positions;
  }, [events, timeRange, timeRangeMs, width]);

  // Get cursor X position relative to this container
  const cursorX = useMemo(() => {
    if (!mousePos || !containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = mousePos.client?.x !== undefined ? mousePos.client.x - rect.left : null;
    return relativeX !== null && relativeX >= 0 && relativeX <= width ? relativeX : null;
  }, [mousePos, width]);

  // Hit testing: find the closest event to the cursor position.
  // Uses a pixel-based buffer (EVENT_HIT_RADIUS_PX) for stability - this avoids
  // tooltip flicker and allows the tooltip to show even when not hovering directly over the icon.
  const detectedEvent = useMemo(() => {
    if (cursorX == null || eventPositions.length === 0) return null;

    // Find the event with minimum horizontal distance from cursor
    let closest: { event: EventItem; distance: number } | null = null;
    for (const pos of eventPositions) {
      const distance = Math.abs(pos.x - cursorX);
      if (closest === null || distance < closest.distance) {
        closest = { event: pos.event, distance };
      }
    }

    // Only return the event if it's within the hit radius
    if (closest && closest.distance <= EVENT_HIT_RADIUS_PX) {
      return closest.event;
    }
    return null;
  }, [cursorX, eventPositions]);

  const eventPanelKey = parentPanelKey ? `${parentPanelKey}-events` : 'events';

  useEffect(() => {
    if (detectedEvent && pinnedPanelKey !== eventPanelKey) {
      lastDetectedEventRef.current = detectedEvent;
    }
  }, [detectedEvent, pinnedPanelKey, eventPanelKey]);

  if (eventPositions.length === 0) {
    return null;
  }

  return (
    <PanelTooltipWrapper
      width={width}
      tooltipConfig={{ enabled: true, enablePinning: true }}
      panelKey={eventPanelKey}
      parentPanelKey={parentPanelKey}
      renderTooltip={({ isPinned, isOverChart }) => {
        if (!isPinned && !isOverChart) return null;
        const eventForTooltip = isPinned ? lastDetectedEventRef.current : detectedEvent;
        if (!eventForTooltip) return null;
        return <EventTooltip event={eventForTooltip} clearPinned={handleUnpin} />;
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: `${width}px`,
          height: `${height}px`,
          position: 'relative',
          borderBottom: (theme) => `1px solid ${theme.palette.border.default}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        {/* Render shaded ranges for grouped events (events with endTimestampMs) */}
        {eventPositions.map((pos, index) => {
          const { event, isEndMarker, x } = pos;
          // Only render range for start marker, skip end markers
          if (isEndMarker || !isEventWithEndTimestamp(event)) return null;
          const startX = x;
          // Clamp endX based on chart width
          const endX = Math.min(width, ((event.endTimestampMs - timeRange.start.getTime()) / timeRangeMs) * width);
          const rangeWidth = Math.max(RANGE_MIN_WIDTH_PX, endX - startX);
          const eventColor =
            event.color ??
            (theme.featureFlags.panwRebrandStubYear ? theme.palette.status.default.main : theme.palette.grey[400]);
          return (
            <Box
              key={`range-${event.timestampMs}-${index}`}
              sx={{
                position: 'absolute',
                left: `${startX}px`,
                top: 0,
                width: `${rangeWidth}px`,
                height: '100%',
                backgroundColor: eventColor,
                opacity: RANGE_OPACITY,
                pointerEvents: 'none', // Do not interfere with event icon clicks
              }}
            />
          );
        })}
        {/* Render event icons on top of shaded ranges */}
        {eventPositions.map(({ event, x, y, isEndMarker }, index) => (
          <EventIcon
            key={`${event.timestampMs}-${isEndMarker ? 'end' : 'start'}-${index}`}
            event={event}
            x={x}
            y={y}
            onClick={onEventClick}
          />
        ))}
      </Box>
    </PanelTooltipWrapper>
  );
}
